import { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Typography,
    Button,
    Stack,
    FormControlLabel,
    Checkbox,
    TextField,
    InputAdornment,
    Divider,
    List,
    MenuItem,
    ListItemIcon,
    Chip,
    CircularProgress,
} from '@mui/material';
import MainCard from 'components/ui/Card/MainCard';
import IconButton from 'components/ui/Button/IconButton';
import { ThemeMode } from 'types/config';
import _ from 'lodash';
import CheckIcon from '@mui/icons-material/Check';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { isProjectExpired } from 'utils/project';
import { GetProjectApiResponse } from 'store/userOrg/userOrgApi';

interface GlobalFilterContentProps {
    projects: GetProjectApiResponse | undefined;
    selectedGlobalFilterValues: string[];
    setGlobalFilter: (shortName: string) => void;
    setGlobalFilters: (shortNames: string[]) => void;
    putPreferencesIsLoading: boolean;
    loading?: boolean;
    maxHeight?: number | string;
    showExpired: boolean;
    setShowExpired: (show: boolean) => void;
}

const GlobalFilterContent = ({
    projects,
    selectedGlobalFilterValues,
    setGlobalFilter,
    setGlobalFilters,
    putPreferencesIsLoading,
    loading,
    maxHeight = 320,
    showExpired,
    setShowExpired,
}: GlobalFilterContentProps) => {
    const theme = useTheme();
    const [searchQuery, setSearchQuery] = useState('');

    const selectedGlobalFilterNames = projects
        ?.filter(project => selectedGlobalFilterValues?.includes(project.ShortName as string))
        .map(project => project?.Name);

    const hasSelectedExpiredProjects = useMemo(() => {
        return (
            projects?.some(
                project =>
                    isProjectExpired(project) && selectedGlobalFilterValues?.includes(project.ShortName as string)
            ) || false
        );
    }, [projects, selectedGlobalFilterValues]);

    const effectiveShowExpired = showExpired || hasSelectedExpiredProjects;

    const activeProjects = projects?.filter(project => !isProjectExpired(project));
    const expiredProjects = projects?.filter(project => isProjectExpired(project));

    const sortedActiveProjects = _.sortBy(activeProjects, [project => project.ShortName]);
    const sortedExpiredProjects = _.sortBy(expiredProjects, [project => project.ShortName]);

    const allSortedProjects = effectiveShowExpired
        ? [...(sortedActiveProjects || []), ...(sortedExpiredProjects || [])]
        : sortedActiveProjects;

    const sortedProjects = searchQuery
        ? allSortedProjects?.filter(
              project =>
                  project.ShortName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  project.Name?.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : allSortedProjects;

    const handleSelectFilteredProjects = () => {
        const filteredProjectShortNames =
            sortedProjects?.map(project => project?.ShortName).filter((v): v is string => typeof v === 'string') || [];
        setGlobalFilters(filteredProjectShortNames);
    };

    const handleShowExpiredChange = (checked: boolean) => {
        setShowExpired(checked);

        if (!checked) {
            const activeProjectShortNames = selectedGlobalFilterValues.filter(shortName => {
                const project = projects?.find(p => p.ShortName === shortName);
                return project && !isProjectExpired(project);
            });

            if (activeProjectShortNames.length !== selectedGlobalFilterValues.length) {
                setGlobalFilters(activeProjectShortNames);
            }
        }
    };

    return (
        <MainCard
            elevation={0}
            border={false}
            content={false}
            sx={{
                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'grey.900' : 'grey.50',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box
                        sx={{
                            p: 1,
                            bgcolor: theme.palette.primary.lighter,
                        }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1,
                            }}>
                            <Typography variant='h6' fontWeight={600} sx={{ color: theme.palette.primary.darker }}>
                                Project Filter
                                <Typography
                                    component='span'
                                    variant='caption'
                                    sx={{ ml: 1, opacity: 0.7, fontWeight: 'normal' }}>
                                    ({selectedGlobalFilterValues?.length || 0}/{sortedProjects?.length || 0})
                                </Typography>
                            </Typography>
                            <TextField
                                size='small'
                                placeholder='Search...'
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SearchIcon fontSize='small' />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery && (
                                        <InputAdornment position='end'>
                                            <IconButton size='small' onClick={() => setSearchQuery('')} edge='end'>
                                                <ClearIcon fontSize='small' />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    bgcolor: 'background.paper',
                                    borderRadius: 1.5,
                                    width: '260px',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.palette.primary.main,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.palette.primary.main,
                                        },
                                    },
                                }}
                            />
                        </Box>

                        <Stack direction='row' spacing={1} alignItems='center'>
                            <Button
                                fullWidth
                                variant='outlined'
                                size='small'
                                color='success'
                                onClick={handleSelectFilteredProjects}
                                disabled={
                                    !sortedProjects?.length ||
                                    selectedGlobalFilterValues?.length === sortedProjects?.length
                                }
                                startIcon={<TaskAltIcon />}
                                sx={{
                                    flex: 1,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                        bgcolor: 'background.paper',
                                        transform: 'translateY(-1px)',
                                        boxShadow: theme.customShadows.z1,
                                    },
                                }}>
                                All
                            </Button>
                            <Button
                                fullWidth
                                variant='outlined'
                                size='small'
                                color='warning'
                                onClick={() => setGlobalFilter('null')}
                                disabled={!sortedProjects?.length || selectedGlobalFilterValues?.length === 0}
                                startIcon={<UnpublishedIcon />}
                                sx={{
                                    flex: 1,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                        bgcolor: 'background.paper',
                                        transform: 'translateY(-1px)',
                                        boxShadow: theme.customShadows.z1,
                                    },
                                }}>
                                None
                            </Button>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={effectiveShowExpired}
                                        onChange={e => handleShowExpiredChange(e.target.checked)}
                                        size='small'
                                        sx={{ p: 0.5 }}
                                    />
                                }
                                label={
                                    <Typography variant='caption' sx={{ userSelect: 'none', fontWeight: 500 }}>
                                        Expired
                                    </Typography>
                                }
                                sx={{ ml: 0, mr: 0, whiteSpace: 'nowrap' }}
                            />
                        </Stack>
                    </Box>

                    <Divider />

                    <Box
                        sx={{
                            maxHeight: maxHeight,
                            overflow: 'auto',
                            bgcolor: 'background.paper',
                            flex: 1,
                        }}>
                        <List component='nav' sx={{ p: 0 }}>
                            {sortedProjects?.map(project => (
                                <MenuItem
                                    key={project.ShortName}
                                    disabled={putPreferencesIsLoading}
                                    onClick={() => project.ShortName && setGlobalFilter(project.ShortName)}
                                    sx={{
                                        mx: 1,
                                        my: 0.5,
                                        px: 2,
                                        py: 1.5,
                                        borderRadius: 1.5,
                                        transition: 'all 0.2s ease',
                                        bgcolor: isProjectExpired(project) ? theme.palette.grey[200] : 'inherit',
                                        '&:hover': {
                                            bgcolor: isProjectExpired(project)
                                                ? theme.palette.grey[300]
                                                : theme.palette.primary.lighter,
                                            transform: 'translateX(4px)',
                                        },
                                    }}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        {selectedGlobalFilterNames?.includes(project.Name) && (
                                            <CheckIcon color='success' fontSize='small' />
                                        )}
                                    </ListItemIcon>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            flex: 1,
                                        }}>
                                        <Typography
                                            variant='body2'
                                            fontWeight={selectedGlobalFilterNames?.includes(project.Name) ? 600 : 400}
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1,
                                            }}>
                                            {project.ShortName}
                                        </Typography>
                                        {isProjectExpired(project) && (
                                            <Chip
                                                label='Expired'
                                                size='small'
                                                color='warning'
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}

                            {(!sortedProjects || sortedProjects.length === 0) && (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        py: 6,
                                        px: 3,
                                    }}>
                                    <Typography variant='body2' color='text.secondary'>
                                        {searchQuery ? 'No projects found' : 'No projects available'}
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    </Box>
                </Box>
            )}
        </MainCard>
    );
};

export default GlobalFilterContent;
